import React, { useState, useEffect } from 'react';
import { Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Box, Modal, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, styled, Switch } from '@mui/material';
import { showToast } from '../../utils/toastUtils';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import ActionButton from '../../components/ActionButton';
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HeaderBox = styled(Box)(({ theme }) => ({
  background: 'rgba(156, 146, 172, 0.2)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .MuiTableCell-root': {
    fontSize: '0.9rem',
    padding: theme.spacing(2),
  },
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1000,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 5,
  borderRadius: 2,
};

const FormContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  '& .MuiFormControl-root': {
    marginBottom: theme.spacing(2),
  },
}));

const PageBackground = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: '1rem 2rem',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.default,
}));


const SmallTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '1.1rem',
  },
}));

const SmallSelect = styled(Select)(({ theme }) => ({
  '& .MuiInputBase-input': {
    padding: '12px 16px',
    fontSize: '1.1rem',
  },
}));

const Products = () => {
  const userIdFromRedux = useSelector(state => state.onboarding?.userId);
  const [organizationId, setOrganizationId] = useState(null);
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newProduct, setNewProduct] = useState({
    productName: '',
    features: '',
    category: '',
    subCategory: '',
    price: '',
    status: '',
    isNewModel: false
  });

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const effectiveUserId = userIdFromRedux || storedUserId;

    if (effectiveUserId) {
      setOrganizationId(effectiveUserId);
      if (!userIdFromRedux) {
        // Dispatch action to update Redux store with the user ID from localStorage
        // You'll need to import useDispatch and the appropriate action creator
        // const dispatch = useDispatch();
        // dispatch(setUserId(effectiveUserId));
      }
    } else {
      showToast('User not found. Please log in again.', 'error');
      // Redirect to login page or handle this case as needed
      // window.location.href = '/login';
    }
  }, [userIdFromRedux]);

  const fetchProducts = async () => {
    if (!organizationId) {
      setError('Organization ID is not available or invalid. Please try logging in again or contact support.');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/api/products`, {
        params: { organizationId }
      });

      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
      } else {
        setProducts([]);
      }

      setLoading(false);
      setError(null);
    } catch (err) {
      const errorMessage = `Failed to fetch products for organizationId: ${organizationId}`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchProducts();
    } else {
      setLoading(false);
      setError('Please log in to view products.');
    }
  }, [organizationId, API_BASE_URL]);


const handleEditProduct = (productId) => {
  setProducts(prevProducts =>
    prevProducts.map(p =>
      p.productId === productId ? { ...p, isEditing: true } : p
    )
  );
};

const handleSaveEditedProduct = async (product) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/api/products/${product.productId}`, {
      ...product,
      organizationId
    });

    if (response.data && response.data.success) {
      setProducts(prevProducts =>
        prevProducts.map(p =>
          p.productId === product.productId ? { ...response.data.product, isEditing: false } : p
        )
      );
      showToast('Product updated successfully', 'success');
    } else {
      throw new Error('Invalid response from the server');
    }
  } catch (error) {
    showToast(`Failed to update product: ${error.message}`, 'error');
  }
};

const handleCancelEdit = (productId) => {
  setProducts(prevProducts =>
    prevProducts.map(p =>
      p.productId === productId ? { ...p, isEditing: false } : p
    )
  );
};

  const handleDeleteProduct = async (productId) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/products`, {
        params: { organizationId, productId }
      });
      
      if (response.data && response.data.message === 'Product deleted successfully') {
        setProducts(products.filter(p => p.productId !== productId));
        showToast('Product deleted successfully', 'success');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'An unexpected error occurred';
      showToast(`Failed to delete product: ${errorMessage}`, 'error');
    }
  };

  const handleStatusChange = async (productId, currentStatus) => {
    const newStatus = currentStatus === 'In Stock' ? 'Out of Stock' : 'In Stock';
    try {
      const response = await axios.put(`${API_BASE_URL}/api/products/${productId}`, {
        organizationId,
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setProducts(products.map(p => 
          p.productId === productId ? { ...p, status: newStatus } : p
        ));
        showToast('Product status updated successfully', 'success');
      } else {
        throw new Error(response.data.message || 'Failed to update product status');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      showToast(`Failed to update product status: ${errorMessage}`, 'error');
    }
  };


const handleAddItem = () => {
  const newEmptyProduct = {
    productId: 'new-' + Date.now(), // Temporary ID
    productName: '',
    features: '',
    category: '',
    subCategory: '',
    price: '',
    status: 'In Stock',
    isNewRow: true
  };
  setProducts([newEmptyProduct, ...products]);
};

const handleNewProductChange = (productId, field, value) => {
  setProducts(prevProducts =>
    prevProducts.map(product =>
      product.productId === productId ? { ...product, [field]: value } : product
    )
  );
};

const handleSaveNewProduct = async (product) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/products`, {
      ...product,
      organizationId,
      price: parseFloat(product.price)
    });

    if (response.data && response.data.productId) {
      await fetchProducts();
      showToast('Product added successfully', 'success');
    } else {
      throw new Error('Invalid response from the server');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Failed to add product';
    showToast(errorMessage, 'error');
  }
};


const handleCancelNewProduct = (productId) => {
  setProducts(prevProducts => prevProducts.filter(p => p.productId !== productId));
};

  const handleCloseModal = () => {
    setOpenModal(false);
    setNewProduct({
      productName: '',
      features: '',
      category: '',
      subCategory: '',
      price: '',
      status: ''
    });
    setEditingProduct(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setNewProduct(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!organizationId) {
      showToast('User ID is not available. Please try logging in again or contact support.', 'error');
      return;
    }

    try {
      const productData = {
        ...newProduct,
        organizationId,
        price: parseFloat(newProduct.price)
      };
      let response;
      if (editingProduct) {
        response = await axios.put(`${API_BASE_URL}/api/products/${editingProduct.productId}`, productData);
      } else {
        response = await axios.post(`${API_BASE_URL}/api/products`, productData);
      }
      if (response.data && (response.data.productId || response.data.success)) {
        let updatedProduct;
        if (editingProduct) {
          updatedProduct = { ...editingProduct, ...productData };
        } else {
          updatedProduct = { ...productData, productId: response.data.productId };
        }
        
        setProducts(prevProducts => 
          editingProduct
            ? prevProducts.map(p => p.productId === editingProduct.productId ? updatedProduct : p)
            : [...prevProducts, updatedProduct]
        );
        
        handleCloseModal();
        showToast(response.data.message || (editingProduct ? 'Product updated successfully' : 'Product added successfully'), 'success');
      } else {
        throw new Error('Invalid response from the server');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
      showToast(`Failed to ${editingProduct ? 'update' : 'add'} product: ${errorMessage}`, 'error');
    }
  };

  // Call fetchProducts after successful product addition
  useEffect(() => {
    fetchProducts();
  }, []);

  if (!organizationId) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">
          Organization ID is not available. Please try logging in again or contact support.
        </Typography>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <PageBackground>
      <Container maxWidth="xl" sx={{ height: 'calc(100vh - 2rem)', display: 'flex', flexDirection: 'column' }}>
        <HeaderBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
            Our Products
          </Typography>
          <ActionButton
            text="Add Item"
            onClick={handleAddItem}
            startIcon={<AddIcon />}
            sx={{ fontSize: '1.2rem', padding: '10px 20px' }}
          />
        </HeaderBox>
        <TableContainer 
          component={Paper} 
          elevation={3} 
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
            height: 'calc(100vh - 200px)', // Adjust this value as needed
            mt: 1,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Table 
            sx={{ 
              width: '100%', 
              minWidth: '1000px',
              tableLayout: 'fixed',
            }} 
            aria-label="product table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '15%', padding: '16px 8px' }}>Name</TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '18%', padding: '16px 8px' }}>Features</TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '13%', padding: '16px 8px' }}>Category</TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '13%', padding: '16px 8px' }}>Sub-category</TableCell>
                <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '10%', padding: '16px 8px' }}>Price</TableCell>
                <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '16%', padding: '16px 8px' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold', width: '15%', padding: '16px 8px' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ overflowY: 'auto' }}>
              {products.map((product) => (
                  <StyledTableRow key={product.productId}>
                  <TableCell component="th" scope="row" sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallTextField
                        fullWidth
                        value={product.productName}
                        onChange={(e) => handleNewProductChange(product.productId, 'productName', e.target.value)}
                        size="small"
                      />
                    ) : (
                      product.productName
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallTextField
                        fullWidth
                        value={product.features}
                        onChange={(e) => handleNewProductChange(product.productId, 'features', e.target.value)}
                        size="small"
                      />
                    ) : (
                      product.features
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallTextField
                        fullWidth
                        value={product.category}
                        onChange={(e) => handleNewProductChange(product.productId, 'category', e.target.value)}
                        size="small"
                      />
                    ) : (
                      product.category
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallTextField
                        fullWidth
                        value={product.subCategory}
                        onChange={(e) => handleNewProductChange(product.productId, 'subCategory', e.target.value)}
                        size="small"
                      />
                    ) : (
                      product.subCategory
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallTextField
                        fullWidth
                        type="number"
                        value={product.price}
                        onChange={(e) => handleNewProductChange(product.productId, 'price', e.target.value)}
                        size="small"
                      />
                    ) : (
                      `$${typeof product.price === 'number' ? product.price.toFixed(2) : 'N/A'}`
                    )}
                  </TableCell>
                  <TableCell sx={{ padding: '16px 8px' }}>
                    {product.isNewRow || product.isEditing ? (
                      <SmallSelect
                        fullWidth
                        value={product.status}
                        onChange={(e) => handleNewProductChange(product.productId, 'status', e.target.value)}
                        size="small"
                      >
                        <MenuItem value="In Stock">In Stock</MenuItem>
                        <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                      </SmallSelect>
                    ) : (
                      <>
                        <Switch
                          checked={product.status === 'In Stock'}
                          onChange={() => handleStatusChange(product.productId, product.status)}
                          color="primary"
                        />
                        {product.status}
                      </>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ padding: '16px 8px' }}>
                    {product.isNewRow ? (
                      <>
                        <IconButton aria-label="save" onClick={() => handleSaveNewProduct(product)} size="small" sx={{ padding: '6px' }}>
                          <SaveIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                        <IconButton aria-label="cancel" onClick={() => handleCancelNewProduct(product.productId)} size="small" sx={{ padding: '6px' }}>
                          <CancelIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                      </>
                    ) : product.isEditing ? (
                      <>
                        <IconButton aria-label="save" onClick={() => handleSaveEditedProduct(product)} size="small" sx={{ padding: '6px' }}>
                          <SaveIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                        <IconButton aria-label="cancel" onClick={() => handleCancelEdit(product.productId)} size="small" sx={{ padding: '6px' }}>
                          <CancelIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton aria-label="edit" onClick={() => handleEditProduct(product.productId)} size="small" sx={{ padding: '6px' }}>
                          <EditIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                        <IconButton aria-label="delete" onClick={() => handleDeleteProduct(product.productId)} size="small" sx={{ padding: '6px' }}>
                          <DeleteIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="product-modal"
          aria-describedby="modal-to-add-or-edit-product"
        >
          <Box sx={modalStyle}>
            <Typography id="product-modal" variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </Typography>
            <form onSubmit={handleSubmit}>
              <FormContainer>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="productName"
                  value={newProduct.productName}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Category"
                  name="category"
                  value={newProduct.category}
                  onChange={handleInputChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Sub-category"
                  name="subCategory"
                  value={newProduct.subCategory}
                  onChange={handleInputChange}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  type="number"
                  value={newProduct.price}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select
                    labelId="status-label"
                    name="status"
                    value={newProduct.status}
                    onChange={handleInputChange}
                    label="Status"
                  >
                    <MenuItem value="In Stock">In Stock</MenuItem>
                    <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Features"
                  name="features"
                  value={newProduct.features}
                  onChange={handleInputChange}
                  multiline
                  rows={2}
                  variant="outlined"
                  sx={{ gridColumn: '1 / -1' }}
                />
              </FormContainer>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <ActionButton
                  text="Cancel"
                  onClick={handleCloseModal}
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <ActionButton
                  text={editingProduct ? 'Update Product' : 'Add Product'}
                  type="submit"
                />
              </Box>
            </form>
          </Box>
        </Modal>
      </Container>
    </PageBackground>
  );
};

export default Products;
